import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { getValidated, AppError } from '@shared/api';
import { userActivityKeys } from './keys';
import { UserActivityListResponseSchema } from './schemas';
import { toUserActivity } from './mappers';

/** Filter options for paginated user activity query. */
export interface UserActivityQueryFilters {
  groupId?: string | null | undefined;
  search?: string | undefined;
  sortOrder?: 'desc' | 'asc' | undefined;
}

/**
 * Infinite query options for the user activity feed (`GET /api/v1/user-activity`).
 * Supports page-based infinite scroll (20 items/page), group filter, and search.
 */
export function userActivityInfiniteQueryOptions(filters?: UserActivityQueryFilters) {
  const groupId = filters?.groupId ?? undefined;
  const rawSearch = filters?.search?.trim();
  const search = rawSearch && rawSearch.length > 0 ? rawSearch : undefined;
  const sortOrder = filters?.sortOrder ?? 'desc';

  return infiniteQueryOptions({
    queryKey: ['user-activity', 'infinite', { groupId, search, sortOrder }],
    queryFn: async ({ pageParam }) => {
      try {
        const queryParams: Record<string, string | number> = {
          page: pageParam,
          sort_order: sortOrder,
        };
        if (groupId) queryParams.group_id = groupId;
        if (search) queryParams.search = search;

        const res = await getValidated(
          'api/v1/user-activity',
          UserActivityListResponseSchema,
          queryParams,
        );
        return res.data?.activities ?? [];
      } catch (error) {
        if (error instanceof AppError && error.status === 404) return [];
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < 20) return undefined;
      return allPages.length + 1;
    },
    select: (data) => {
      const seen = new Set<string>();
      const deduplicated = [];
      for (const page of data.pages) {
        for (const act of page) {
          const key = act._id;
          if (!seen.has(key)) {
            seen.add(key);
            deduplicated.push(toUserActivity(act));
          }
        }
      }
      return deduplicated;
    },
  });
}

export function useUserActivityInfinite(filters?: UserActivityQueryFilters) {
  return useInfiniteQuery(userActivityInfiniteQueryOptions(filters));
}

/**
 * The current user's recent activity feed (`GET /api/v1/user-activity`). Mapping in `select` keeps
 * the DTO (and the lang-path resolution) out of components. 404 is treated as empty.
 */
export const userActivityQueryOptions = queryOptions({
  queryKey: userActivityKeys.all(),
  queryFn: async () => {
    try {
      const res = await getValidated('api/v1/user-activity', UserActivityListResponseSchema);
      return res.data?.activities ?? [];
    } catch (error) {
      if (error instanceof AppError && error.status === 404) return [];
      throw error;
    }
  },
  select: (activities) => activities.map(toUserActivity),
});

export function useUserActivity() {
  return useQuery(userActivityQueryOptions);
}

/**
 * A single group's activity feed (`GET /api/v1/group/{groupId}/activity`). Same envelope + row shape
 * as the global feed, so it reuses the schema + mapper. Only runs when a group id is provided.
 */
export function groupActivityQueryOptions(groupId: string) {
  return queryOptions({
    queryKey: userActivityKeys.group(groupId),
    queryFn: async () => {
      try {
        const res = await getValidated(
          `api/v1/group/${groupId}/activity`,
          UserActivityListResponseSchema,
        );
        return res.data?.activities ?? [];
      } catch (error) {
        if (error instanceof AppError && error.status === 404) return [];
        throw error;
      }
    },
    enabled: Boolean(groupId),
    select: (activities) => activities.map(toUserActivity),
  });
}

export function useGroupActivity(groupId: string) {
  return useQuery(groupActivityQueryOptions(groupId));
}
